import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  Animated,
  Dimensions,
  Image,
  StyleSheet,
  Easing,
} from "react-native";
import { useNavigate } from "react-router-dom";

import {
  PrimaryButton,
  SecondaryButton,
  beforeAfterSteps,
  StickyOnboardingModal,
  RainbowBorder,
} from "../../../../common/native";
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
// import { useOnboardingSettings } from "../../../../../contexts/OnboardingSettingsContext";

const screenWidth = Dimensions.get("window").width;
const imageWidth = screenWidth * 0.7;
const imageSpacing = 20;
const totalImageWidth = imageWidth + imageSpacing;
const MAX_CONTENT_WIDTH = 800;

const SAMPLE_IMAGES = [
  "https://clinic-os.com/user-app-assets/template-images/Pasted%20image%20(2).png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_1.png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_2.png",
  "https://clinic-os.com/user-app-assets/template-images/Pasted%20image%20(3).png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_3.png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_4.png",
  "https://clinic-os.com/user-app-assets/template-images/Pasted%20image%20(4).png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_5.png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_6.png",
  "https://clinic-os.com/user-app-assets/template-images/sample_generated_template_7.png",
];

const images = [...SAMPLE_IMAGES, ...SAMPLE_IMAGES]; // Double for looping

type BeforeAfterLandingPageProps = {
  setStep: (n: number) => void;
  tipsEnabled?: boolean; // note the ?  → now it’s optional
};

const BeforeAfterLandingPage: React.FC<BeforeAfterLandingPageProps> = ({
  setStep,
  tipsEnabled = false,
}) => {
  const scrollX = useRef(new Animated.Value(0)).current;
  const navigate = useNavigate();
  const { tipsState, markStepComplete } = useOnboardingTips();

  useEffect(() => {
    const animate = () => {
      scrollX.setValue(0);

      Animated.timing(scrollX, {
        toValue: -totalImageWidth * SAMPLE_IMAGES.length,
        duration: 160000,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start(() => {
        animate(); // seamless restart
      });
    };

    animate();
  }, []);

  const translateX = scrollX.interpolate({
    inputRange: [-totalImageWidth * SAMPLE_IMAGES.length, 0],
    outputRange: [-totalImageWidth * SAMPLE_IMAGES.length, 0],
  });
  const infoItems = [
    { icon: "💼", text: "Save time. Focus on patients, not Photoshop" },
    { icon: "⏱️", text: "No more manual editing" },
    { icon: "🧠", text: "AI pairs, crops, and formats for you" },
    // { icon: "📁", text: "Upload all your images — we handle the rest" },
    { icon: "🤝", text: "Share stunning results" },
  ];

  return (
    <View style={styles.container}>
      <View style={styles.carouselWrapper}>
        <View style={styles.carouselContainer}>
          <Animated.View
            style={{
              flexDirection: "row",
              transform: [{ translateX }],
            }}
          >
            {images.map((uri, index) => (
              <Image
                key={index}
                source={{ uri }}
                style={styles.carouselImage}
                resizeMode="cover"
              />
            ))}
          </Animated.View>
        </View>
      </View>
      <RainbowBorder
        isActive={
          tipsEnabled &&
          tipsState.startBeforeAfter &&
          !tipsState.usePlaceholderImages
        }
        borderRadius={24}
      >
        <PrimaryButton
          text="Create Before-Afters"
          width={240}
          roundBorderRadius
          onPress={() => {
            if (tipsState.startBeforeAfter && !tipsState.usePlaceholderImages) {
              markStepComplete("startBeforeAfter");
            }
            setStep(1);
          }}
        />
      </RainbowBorder>
      <SecondaryButton
        text="View Your Gallery"
        width={240}
        onPress={() => navigate("/gallery")}
        roundBorderRadius
      />
      <View style={styles.infoCardContainer}>
        {infoItems.map((item, index) => (
          <View key={index} style={styles.infoCard}>
            <Text style={styles.cardIcon}>{item.icon}</Text>
            <Text style={styles.cardText}>{item.text}</Text>
          </View>
        ))}
      </View>
      {tipsEnabled && <StickyOnboardingModal steps={beforeAfterSteps} />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
    paddingVertical: 20,
    alignItems: "center",
    gap: 24,
  },
  carouselWrapper: {
    maxWidth: MAX_CONTENT_WIDTH,
    width: "100%",
    paddingHorizontal: 40, // prevents overlap with side nav
    alignItems: "center",
  },
  carouselContainer: {
    height: 200,
    overflow: "hidden",
    width: "100%",
    alignSelf: "center", // keep it centered
  },
  carouselImage: {
    width: 200,
    height: 200,
    borderRadius: 12,
    marginRight: imageSpacing,
  },
  infoContainer: {
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    maxWidth: MAX_CONTENT_WIDTH,
    width: "100%",
  },
  infoText: {
    fontSize: 16,
    fontFamily: "Urbanist500",
    textAlign: "center",
  },
  headline: {
    fontSize: 20,
    // fontWeight: "600",
    fontFamily: "Urbanist600",
    marginBottom: 8,
    textAlign: "center",
  },
  infoCardContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 12,
    marginTop: 8,
    paddingHorizontal: 16,
    maxWidth: MAX_CONTENT_WIDTH,
  },

  infoCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FB",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    width: "45%",
    minWidth: 160,
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
  },

  cardIcon: {
    fontSize: 18,
  },

  cardText: {
    fontSize: 14,
    fontFamily: "Urbanist500",
    flexShrink: 1,
  },
});

export default BeforeAfterLandingPage;
